/**
 * Sticky Add-to-Bag Bar
 */
const StickyAtb = {
  selectors: {
    bar: '[data-sticky-atb]',
    mainAddButton: '[data-add-to-cart]',
    mainForm: '[data-product-form]',
    sizeButtons: '[data-sticky-atb-size]',
    addButton: '[data-sticky-atb-add]',
    buttonText: '[data-sticky-atb-button-text]',
    priceEl: '[data-sticky-atb-price]',
    productJson: '[data-sticky-atb-product]',
    variantInput: '[data-product-form] input[name="id"]',
    pairWarning: '[data-sticky-atb-pair-warning]',
    pairSelector: '[data-pair-selector]'
  },

  classes: {
    visible: 'is-visible',
    loading: 'is-loading',
    selected: 'sticky-atb__size--selected',
    buttonHidden: 'sticky-atb__button--hidden'
  },

  init() {
    this.bar = document.querySelector(this.selectors.bar);
    if (!this.bar) return;

    this.mainAddButton = document.querySelector(this.selectors.mainAddButton);
    this.mainForm = document.querySelector(this.selectors.mainForm);
    this.sizeButtons = this.bar.querySelectorAll(this.selectors.sizeButtons);
    this.addButton = this.bar.querySelector(this.selectors.addButton);
    this.buttonText = this.bar.querySelector(this.selectors.buttonText);
    this.priceEl = this.bar.querySelector(this.selectors.priceEl);
    this.pairWarning = this.bar.querySelector(this.selectors.pairWarning);
    this.pairSelector = document.querySelector(this.selectors.pairSelector);

    this.requiresPair = this.bar.dataset.requiresPair === 'true';
    this.hasPairSelected = false;

    const productJsonEl = document.querySelector(this.selectors.productJson);
    if (productJsonEl) {
      try {
        this.product = JSON.parse(productJsonEl.textContent);
      } catch (e) {
        console.error('StickyAtb: Failed to parse product JSON', e);
        return;
      }
    }

    this.setupObserver();
    this.bindEvents();
  },

  setupObserver() {
    if (!this.mainAddButton) return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const rect = this.mainAddButton.getBoundingClientRect();
        const isAboveViewport = rect.bottom < 0;

        if (!entry.isIntersecting && isAboveViewport) {
          this.show();
        } else {
          this.hide();
        }
      });
    }, options);

    this.observer.observe(this.mainAddButton);
  },

  show() {
    this.bar.classList.add(this.classes.visible);
  },

  hide() {
    this.bar.classList.remove(this.classes.visible);
  },

  bindEvents() {
    this.sizeButtons.forEach((btn) => {
      btn.addEventListener('click', this.handleSizeClick.bind(this));
    });

    if (this.addButton) {
      this.addButton.addEventListener('click', this.handleAddToCart.bind(this));
    }

    if (this.pairWarning) {
      this.pairWarning.addEventListener('click', this.handlePairWarningClick.bind(this));
    }

    document.addEventListener('theme:variant:change', this.handleVariantChange.bind(this));

    if (this.requiresPair) {
      document.addEventListener('theme:product:changed', this.handlePairChange.bind(this));
    }
  },

  handlePairWarningClick(e) {
    e.preventDefault();
    if (this.pairSelector) {
      const offset = 100;
      const top = this.pairSelector.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  },

  handlePairChange(e) {
    const hasPartner = e.detail?.hasPartner || false;
    this.hasPairSelected = hasPartner;
    this.updatePairState();
  },

  updatePairState() {
    if (!this.requiresPair) return;

    if (this.hasPairSelected) {
      if (this.pairWarning) this.pairWarning.style.display = 'none';
      if (this.addButton) this.addButton.classList.remove(this.classes.buttonHidden);
    } else {
      if (this.pairWarning) this.pairWarning.style.display = '';
      if (this.addButton) this.addButton.classList.add(this.classes.buttonHidden);
    }
  },

  handleSizeClick(e) {
    const btn = e.currentTarget;
    if (btn.disabled) return;

    const variantId = btn.dataset.variantId;
    const optionValue = btn.dataset.optionValue;

    this.updateSelectedSize(btn);
    this.updateAddButton(variantId);
    this.syncMainForm(optionValue);
  },

  updateSelectedSize(selectedBtn) {
    this.sizeButtons.forEach((btn) => {
      btn.classList.remove(this.classes.selected);
    });
    selectedBtn.classList.add(this.classes.selected);
  },

  updateAddButton(variantId) {
    const variant = this.product.variants.find(v => v.id === parseInt(variantId));
    if (!variant) return;

    this.addButton.dataset.variantId = variantId;

    if (variant.available) {
      this.addButton.disabled = false;
      this.buttonText.textContent = theme.strings.addToCart || 'Add to bag';
    } else {
      this.addButton.disabled = true;
      this.buttonText.textContent = theme.strings.soldOut || 'Sold out';
    }

    if (this.priceEl) {
      const formattedPrice = theme.settings.currency_code_enable
        ? this.formatMoney(variant.price) + ' ' + theme.currencyCode
        : this.formatMoney(variant.price);
      this.priceEl.textContent = formattedPrice;
    }
  },

  formatMoney(cents) {
    if (typeof themeCurrency !== 'undefined' && themeCurrency.formatMoney) {
      return themeCurrency.formatMoney(cents, theme.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  },

  syncMainForm(optionValue) {
    if (!this.mainForm) return;

    const radioInput = this.mainForm.querySelector(`input[type="radio"][value="${optionValue}"]`);
    if (radioInput && !radioInput.checked) {
      radioInput.checked = true;
      radioInput.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const selectInput = this.mainForm.querySelector('select[data-product-select]');
    if (selectInput) {
      const optionToSelect = Array.from(selectInput.options).find(opt =>
        opt.textContent.includes(optionValue)
      );
      if (optionToSelect) {
        selectInput.value = optionToSelect.value;
        selectInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  },

  handleVariantChange(e) {
    const variant = e.detail?.variant;
    if (!variant) return;

    const matchingBtn = Array.from(this.sizeButtons).find(
      btn => parseInt(btn.dataset.variantId) === variant.id
    );

    if (matchingBtn) {
      this.updateSelectedSize(matchingBtn);
    }

    this.updateAddButton(variant.id);
  },

  async handleAddToCart(e) {
    e.preventDefault();

    const variantId = this.addButton.dataset.variantId;
    if (!variantId || this.addButton.disabled) return;

    // If pair is required, delegate to the main add-to-cart button
    // The pair selector intercepts that button and adds both products
    if (this.requiresPair && this.hasPairSelected && this.mainAddButton) {
      this.addButton.classList.add(this.classes.loading);
      this.mainAddButton.click();
      // Reset loading state after a short delay (pair selector handles the rest)
      setTimeout(() => {
        this.addButton.classList.remove(this.classes.loading);
      }, 500);
      return;
    }

    this.addButton.classList.add(this.classes.loading);
    this.addButton.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: 1
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add to cart');
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      document.dispatchEvent(new CustomEvent('theme:cart:change', {
        detail: { cart: cart },
        bubbles: true
      }));

      document.dispatchEvent(new CustomEvent('theme:cart:open', {
        bubbles: true
      }));

    } catch (error) {
      console.error('StickyAtb: Add to cart failed', error);
      alert(error.message || 'Could not add item to cart. Please try again.');
    } finally {
      this.addButton.classList.remove(this.classes.loading);
      const variant = this.product.variants.find(v => v.id === parseInt(variantId));
      this.addButton.disabled = !(variant && variant.available);
    }
  }
};

document.addEventListener('DOMContentLoaded', () => {
  StickyAtb.init();
});
